/**
 * Lightweight data-fetching hook. Replace with TanStack Query later
 * if cache invalidation becomes a real burden — for Sprint 5 this is
 * enough.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { ApiError } from "../api/client";


export interface UseFetchState<T> {
  data:    T | null;
  error:   Error | null;
  loading: boolean;
  refetch: () => void;
}


export function useFetch<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  /** Restart fetch when any of these change. Pass [] for "fetch once". */
  deps: ReadonlyArray<unknown>,
): UseFetchState<T> {
  const [data, setData]       = useState<T | null>(null);
  const [error, setError]     = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [tick, setTick]       = useState<number>(0);
  const aborterRef            = useRef<AbortController | null>(null);

  useEffect(() => {
    const aborter = new AbortController();
    aborterRef.current = aborter;
    setLoading(true);
    setError(null);

    fetcher(aborter.signal)
      .then((result) => {
        if (!aborter.signal.aborted) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        if (!aborter.signal.aborted) {
          setError(e instanceof Error ? e : new Error(String(e)));
          setLoading(false);
        }
      });

    return () => aborter.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, loading, refetch };
}


export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}
